import { resolveSupplyDates, type SkuLeadTimes } from "~/utils/productionDate"
import {
  DEFAULT_PO_GRANULARITY,
  poGroupKey,
  poGroupNameDiscriminator,
  type PoGranularity,
} from "~/utils/poGranularity"
import { largest, groupBy } from "~/utils/tsUtils"

export type NoosSkuMeta = {
  vendorId: string
  vendorName: string
  variantId: string
  variantName?: string | null
  productId?: string | null
  productName?: string | null
  leadTimes: SkuLeadTimes
  // Per-warehouse shipping leadtimes from the SKU's vendor's procurementSettings. When
  // populated, ETA is computed per warehouse so the vendor produces in one batch (aligned
  // ETD) but each warehouse arrives based on its own shipping leg.
  vendorShippingLeadTimes?: Record<string, number>
}

function noosEta(orderDate: Date, meta: NoosSkuMeta, warehouseId: string): Date {
  return resolveSupplyDates({
    anchor: { kind: "opd", date: orderDate },
    sku: meta.leadTimes,
    vendorShippingLeadTimes: meta.vendorShippingLeadTimes,
    warehouseId,
  }).eta
}

type SuggestedOrderItem = {
  skuId: string
  warehouseId: string
  quantity: number
}

type NoosPoBatchGroup = {
  vendor: { id: string; name: string }
  items: { skuId: string; warehouseId: string; quantity: number }[]
  addedDemandWarehouseIds: string[]
  nameDiscriminator?: string
}

export type NoosPoBatch = {
  expectedArrivalDate: Date
  purchasePeriodEndDate: Date
  collectionIds?: string[]
  groups: NoosPoBatchGroup[]
}

type BuildNoosPoBatchesOptions = {
  suggestedOrders: SuggestedOrderItem[]
  skuMetaMap: Map<string, NoosSkuMeta>
  warehouseAliasMap: Map<string, string>
  effectiveWarehouseSet: Set<string>
  syncPreorderQuantitiesMap: Record<string, boolean>
  preordersMap?: Map<string, Map<string, number>>
  demandMap: Record<string, string[]>
  onlySuggestedSkus: boolean
  orderPlacementDate: Date
  nextBuyDate: Date
  collectionIds?: string[]
  /**
   * When true, every SKU in `skuMetaMap` gets a row for each warehouse in
   * `effectiveWarehouseSet`, defaulting to qty 0 when the suggestion engine produced
   * nothing for that pair. Required by the Buy flow so users can later edit any
   * (sku × warehouse) cell — including for variants the suggestion engine had no
   * demand for at all (e.g. MOQ top-ups, safety buffer).
   */
  ensureRowPerActiveWarehouse?: boolean
  /** Refines the vendor axis of the grouping. Defaults to `vendor` (unchanged behaviour). */
  granularity?: PoGranularity
}

/**
 * Builds PO creation batches grouped by warehouse × vendor × lead time.
 * One batch per (warehouseId, expectedArrivalDate), one PO per vendor within each batch —
 * or per vendor × product / vendor × variant when `granularity` narrows it.
 * Designed for reuse across both the NOOS dialog and the automatic NOOS buy trigger.
 */
export function buildNoosPoBatches({
  suggestedOrders,
  skuMetaMap,
  warehouseAliasMap,
  effectiveWarehouseSet,
  syncPreorderQuantitiesMap,
  preordersMap,
  demandMap,
  onlySuggestedSkus,
  orderPlacementDate,
  nextBuyDate,
  collectionIds,
  ensureRowPerActiveWarehouse = false,
  granularity = DEFAULT_PO_GRANULARITY,
}: BuildNoosPoBatchesOptions): NoosPoBatch[] {
  let items = suggestedOrders

  if (onlySuggestedSkus) {
    const variantsWithSuggestions = new Set<string>()
    for (const item of suggestedOrders) {
      if (item.quantity > 0) {
        const meta = skuMetaMap.get(item.skuId)
        if (meta) variantsWithSuggestions.add(meta.variantId)
      }
    }
    items = suggestedOrders.filter((item) => {
      const meta = skuMetaMap.get(item.skuId)
      return meta && variantsWithSuggestions.has(meta.variantId)
    })
  }

  type MergedItem = {
    skuId: string
    warehouseId: string
    quantity: number
    expectedArrivalDate: Date
  }
  const mergedItemsMap = new Map<string, MergedItem>()

  for (const item of items) {
    const mergedWarehouseId = warehouseAliasMap.get(item.warehouseId) ?? item.warehouseId
    if (!effectiveWarehouseSet.has(mergedWarehouseId)) continue

    const meta = skuMetaMap.get(item.skuId)
    if (!meta) continue

    const expectedArrivalDate = noosEta(orderPlacementDate, meta, mergedWarehouseId)

    let qty = item.quantity
    if (syncPreorderQuantitiesMap[mergedWarehouseId] && preordersMap) {
      qty = preordersMap.get(item.skuId)?.get(item.warehouseId) ?? 0
    }

    const key = `${mergedWarehouseId}_${item.skuId}`
    if (!mergedItemsMap.has(key)) {
      mergedItemsMap.set(key, {
        skuId: item.skuId,
        warehouseId: mergedWarehouseId,
        quantity: qty,
        expectedArrivalDate,
      })
    } else {
      const existing = mergedItemsMap.get(key)!
      mergedItemsMap.set(key, { ...existing, quantity: existing.quantity + qty })
    }
  }

  if (ensureRowPerActiveWarehouse) {
    for (const [skuId, meta] of skuMetaMap) {
      for (const warehouseId of effectiveWarehouseSet) {
        const key = `${warehouseId}_${skuId}`
        if (!mergedItemsMap.has(key)) {
          const expectedArrivalDate = noosEta(orderPlacementDate, meta, warehouseId)
          mergedItemsMap.set(key, { skuId, warehouseId, quantity: 0, expectedArrivalDate })
        }
      }
    }
  }

  const mergedItems = Array.from(mergedItemsMap.values())
  const byWarehouseArrival = groupBy(
    mergedItems,
    (item) => `${item.warehouseId}__${item.expectedArrivalDate.toISOString()}`,
  )

  const batches: NoosPoBatch[] = []

  for (const groupItems of Object.values(byWarehouseArrival)) {
    const first = groupItems[0]!
    const { warehouseId, expectedArrivalDate } = first

    const byGroup = groupBy(groupItems, (item) => {
      const meta = skuMetaMap.get(item.skuId)
      if (!meta) return "noVendor"
      return poGroupKey(granularity, meta)
    })

    const groups: NoosPoBatchGroup[] = Object.values(byGroup)
      .map((poItems) => {
        const meta = skuMetaMap.get(poItems[0]!.skuId)
        const vendorId = meta?.vendorId ?? "noVendor"
        return {
          vendor: { id: vendorId, name: meta?.vendorName ?? vendorId },
          nameDiscriminator: meta ? poGroupNameDiscriminator(granularity, meta) : undefined,
          // Quantities go out as planned; colli is resolved on the buy, never here (SUP-414).
          items: poItems.map((item) => ({
            skuId: item.skuId,
            warehouseId,
            quantity: Math.ceil(largest(item.quantity, 0) ?? 0),
          })),
          addedDemandWarehouseIds: demandMap[warehouseId] ?? [],
        }
      })
      .filter((group) => group.items.length > 0)

    if (groups.length > 0) {
      // periodEnd mirrors ETA: just as ETA = orderPlacementDate + leadTime, the period
      // ends when the *next* buy of this same vendor will arrive: nextBuyDate + leadTime.
      // This keeps the forecast window length = purchaseInterval regardless of lead time.
      const leadTimeMs = expectedArrivalDate.getTime() - orderPlacementDate.getTime()
      const purchasePeriodEndDate = new Date(nextBuyDate.getTime() + leadTimeMs)
      batches.push({
        expectedArrivalDate,
        purchasePeriodEndDate,
        collectionIds,
        groups,
      })
    }
  }

  return batches
}
